import { REPORT_BUNDLE_GLOBAL, ReportBundleRuntime } from '@/reports/bundles/types';
import { REPORT_CONTRACT_VERSION, ReportDefinition, ReportEvaluation, ReportValues } from '@/reports/types';
import { installReportHost } from './host';
import { ReportComponents } from './types';

/**
 * Evaluating a compiled report bundle against the host installed by host.ts.
 * Kept apart from loader.ts, which fetches runtimes through the app's client,
 * so the SDK kit's CLI can load a bundle in Node without the app around it.
 */

export type AnyReportDefinition = ReportDefinition<ReportValues, ReportEvaluation>;

export interface LoadedReportModule {
  definition: AnyReportDefinition;
  components: ReportComponents;
  bundleVersionId: string;
}

/** Run the two compiled scripts and check what they export. Also how the studio previews a draft. */
export function evaluateReportRuntime(runtime: ReportBundleRuntime): LoadedReportModule {
  if (runtime.contractVersion !== REPORT_CONTRACT_VERSION) {
    throw new Error(`report "${runtime.key}" was built against contract ${runtime.contractVersion}; this app runs contract ${REPORT_CONTRACT_VERSION}`);
  }
  installReportHost();
  const definitionExports = runScript(runtime.definitionJs) as { definition?: AnyReportDefinition };
  const documentExports = runScript(runtime.documentJs) as Partial<ReportComponents>;
  const definition = definitionExports?.definition;
  if (!definition || (typeof definition.evaluate !== 'function' && typeof definition.judge !== 'function')) {
    throw new Error(`report "${runtime.key}" exports no definition`);
  }
  if (typeof definition.evaluate !== 'function' && definition.judge) definition.evaluate = definition.judge;
  if (typeof documentExports?.Document !== 'function') {
    throw new Error(`report "${runtime.key}" exports no Document component`);
  }
  return {
    definition,
    components: { Document: documentExports.Document },
    bundleVersionId: runtime.bundleVersionId,
  };
}

/** The compiled script declares `var __veriformReportBundle = …`; inside a Function body that stays local. */
function runScript(js: string): unknown {
  return new Function(`${js}\nreturn ${REPORT_BUNDLE_GLOBAL};`)();
}
